// mcpToolProjectionsApi — dashboard client for the per-tenant MCP tool-visibility
// override CRUD. Talks to runtime-api's control-plane endpoints:
//   GET    /v1/runtime/mcp-tool-projections
//   POST   /v1/runtime/mcp-tool-projections                 { tool_name, visibility, context_predicate?, lifecycle? }
//   PUT    /v1/runtime/mcp-tool-projections/:id             { visibility, context_predicate?, lifecycle? }
//   DELETE /v1/runtime/mcp-tool-projections/:id
//
// Spec: docs/specs/nexus-mcp-tool-projection-policy-v0.md (the per-tenant
// override language + admin CRUD). Backend: apps/runtime-api/src/index.ts
// (validateProjectionPayload + projectionView, shipped #774). All endpoints
// require the RBAC permission mcp:manage_projection (default tenant_admin).
//
// These overrides only ever TIGHTEN (hide / context-gate) which MCP tools a
// caller — whose permissions already allow the tool — can see and invoke; an
// override can NEVER grant a tool. The change affects what MCP clients in this
// tenant may both LIST and CALL.
import { apiFetch } from "../../lib/api-client.js";

/** Visibility of a tool for callers in this tenant. `visible` = baseline
 *  (permission-only); `hidden` = always withheld; `context_gated` = shown only
 *  when the stored predicate evaluates true against the caller's context. */
export type ProjectionVisibility = "visible" | "hidden" | "context_gated";

/** Override lifecycle. `active` rows are read by mcp-api; `inactive` is a soft
 *  off-switch (the tool reverts to the permission baseline) without deleting. */
export type ProjectionLifecycle = "active" | "inactive";

// ── The context_gated predicate language (mirror of @nexus/shared
// mcp-projection `Predicate` — kept in sync; the server re-validates with the
// SAME shared validator). The supported operator set: ────────────────────────
//   { op: "role_in", roles: string[] }
//   { op: "has_permission", permission: string }
//   { op: "has_any_permission", permissions: string[] }
//   { op: "environment_in", environments: ("DEV"|"UAT"|"PROD")[] }   deploy env
//   { op: "all", of: Predicate[] }   conjunction
//   { op: "any", of: Predicate[] }   disjunction
export type PredicateEnvironment = "DEV" | "UAT" | "PROD";

export type Predicate =
  | { op: "role_in"; roles: string[] }
  | { op: "has_permission"; permission: string }
  | { op: "has_any_permission"; permissions: string[] }
  | { op: "environment_in"; environments: PredicateEnvironment[] }
  | { op: "all"; of: Predicate[] }
  | { op: "any"; of: Predicate[] };

/** One override row as returned by runtime-api (`projectionView`). The
 *  predicate is returned PARSED (or null for visible / hidden). */
export interface ProjectionRow {
  id: string;
  tool_name: string;
  visibility: ProjectionVisibility;
  /** Parsed predicate JSON for context_gated rows; null otherwise. */
  context_predicate: Predicate | null;
  lifecycle: ProjectionLifecycle;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectionListResponse {
  projections: ProjectionRow[];
  tenant_id: string;
  correlation_id: string | null;
}

export interface ProjectionMutationResponse {
  projection: ProjectionRow | null;
  tenant_id: string;
  correlation_id: string | null;
}

/** Payload shared by create / update. `tool_name` is only sent on create
 *  (immutable thereafter). `context_predicate` is REQUIRED for context_gated
 *  and must be ABSENT for visible / hidden (server rejects otherwise). */
export interface ProjectionWritePayload {
  tool_name?: string;
  visibility: ProjectionVisibility;
  context_predicate?: Predicate;
  lifecycle?: ProjectionLifecycle;
}

export class McpProjectionApiError extends Error {
  status: number;
  code: string;
  correlationId: string | null;
  upstreamMessage?: string;
  constructor(status: number, code: string, cid: string | null, msg?: string) {
    super(code);
    this.status = status;
    this.code = code;
    this.correlationId = cid;
    this.upstreamMessage = msg;
  }
}

export function friendlyMcpProjectionError(err: unknown): string {
  if (err instanceof McpProjectionApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403)
      return "您目前的角色不能管理 MCP 工具可見性（需要「管理 MCP 工具可見性覆蓋」權限）。";
    if (err.status === 404 && err.code === "projection_not_found")
      return "找不到這筆覆蓋設定（可能已被其他人刪除，請重新整理）。";
    if (err.status === 404 && err.code === "tenant_not_routed") return "此客戶單位尚未配置資料分片。";
    if (err.status === 404) return "找不到這筆覆蓋設定。";
    if (err.status === 409 && err.code === "projection_already_exists_for_tool")
      return "這個工具已經有一筆覆蓋設定了；請改為編輯既有那一筆，而不是再新增。";
    if (err.code === "unknown_tool_name") return "選到的工具不在可投影的工具清單內，請重新選擇。";
    if (err.code === "tool_name_required") return "請先選擇一個工具。";
    if (err.code === "visibility_must_be_visible_hidden_or_context_gated")
      return "可見性設定不正確，請重新選擇。";
    if (err.code === "lifecycle_must_be_active_or_inactive") return "啟用狀態設定不正確。";
    if (err.code === "context_gated_requires_context_predicate")
      return "設成「依情境顯示」時必須設定至少一個條件。";
    if (err.code === "context_predicate_only_allowed_for_context_gated")
      return "只有「依情境顯示」才需要設定條件；「顯示」或「隱藏」不需要條件。";
    if (err.code.startsWith("invalid_context_predicate:"))
      return `條件設定不完整或格式不正確（${err.code.slice("invalid_context_predicate:".length)}），請檢查每個條件都有填妥。`;
    if (err.status === 400) return `請求格式不正確（${err.code}）。`;
    return `服務暫時無法存取（HTTP ${err.status}・code=${err.code}・cid=${err.correlationId ?? "n/a"}）。`;
  }
  return "連線中斷，請檢查網路後再試。";
}

async function asError(r: Response): Promise<McpProjectionApiError> {
  let code = "request_failed";
  let cid: string | null = r.headers.get("x-correlation-id");
  let msg: string | undefined;
  try {
    const b = (await r.json()) as { error?: string; correlation_id?: string; message?: string };
    code = b.error ?? code;
    cid = b.correlation_id ?? cid;
    msg = b.message;
  } catch {
    /* non-JSON error body — keep defaults */
  }
  return new McpProjectionApiError(r.status, code, cid, msg);
}

function tenantQs(tenantId?: string | null): string {
  return tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
}

export async function listMcpToolProjections(
  params: { tenantId?: string | null; limit?: number } = {},
): Promise<ProjectionListResponse> {
  const qs = new URLSearchParams();
  if (params.tenantId) qs.set("tenant_id", params.tenantId);
  if (params.limit) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs}` : "";
  const r = await apiFetch(`/v1/runtime/mcp-tool-projections${suffix}`);
  if (!r.ok) throw await asError(r);
  return (await r.json()) as ProjectionListResponse;
}

export async function createMcpToolProjection(
  payload: ProjectionWritePayload,
  tenantId?: string | null,
): Promise<ProjectionMutationResponse> {
  const r = await apiFetch(`/v1/runtime/mcp-tool-projections${tenantQs(tenantId)}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as ProjectionMutationResponse;
}

export async function updateMcpToolProjection(
  id: string,
  payload: Omit<ProjectionWritePayload, "tool_name">,
  tenantId?: string | null,
): Promise<ProjectionMutationResponse> {
  const r = await apiFetch(`/v1/runtime/mcp-tool-projections/${encodeURIComponent(id)}${tenantQs(tenantId)}`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as ProjectionMutationResponse;
}

export async function deleteMcpToolProjection(
  id: string,
  tenantId?: string | null,
): Promise<void> {
  const r = await apiFetch(`/v1/runtime/mcp-tool-projections/${encodeURIComponent(id)}${tenantQs(tenantId)}`, {
    method: "DELETE",
  });
  if (!r.ok) throw await asError(r);
}
